<template>
  <div class="inv-list-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <!-- ===== Hero ===== -->
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Facturas / Listado</p>
            <h1 class="list-title">Listado de Facturas</h1>
            <p class="list-subtitle">
              Consulte y gestione todas las facturas creadas en la plataforma.
              Las facturas se agrupan por trámite: consolidado (CONS-) o
              individual (IND-).
            </p>
          </div>
        </v-flex>

        <!-- ===== Filtro ===== -->
        <v-flex xs12>
          <v-card class="inv-card">
            <v-card-text class="inv-filter">
              <v-layout row wrap>
                <v-flex md3 sm12 xs12>
                  <label class="inv-label">Buscar</label>
                  <v-text-field
                    v-model="filter.term"
                    class="inv-input"
                    append-icon="search"
                    placeholder="# factura, consecutivo (CONS-/IND-), # control, bulto, CIF o laboratorio..."
                    clearable
                    solo
                    flat
                    hide-details
                    @keyup.enter="onSearch"
                  ></v-text-field>
                </v-flex>
                <v-flex md2 sm6 xs12>
                  <label class="inv-label">Estado del trámite</label>
                  <v-select
                    v-model="filter.paso"
                    class="inv-input"
                    :items="pasos"
                    item-text="texto"
                    item-value="valor"
                    placeholder="Todos"
                    no-data-text="Datos no disponibles"
                    clearable
                    solo
                    flat
                    hide-details
                    @change="onSearch"
                  >
                    <template v-slot:selection="{ item }">
                      <span class="inv-sel">{{ item.texto }}</span>
                    </template>
                  </v-select>
                </v-flex>
                <v-flex md2 sm6 xs12>
                  <label class="inv-label">Distribuidor</label>
                  <v-select
                    v-model="filter.distId"
                    class="inv-input"
                    :items="distributors"
                    item-text="name"
                    item-value="id"
                    placeholder="Todos"
                    no-data-text="Datos no disponibles"
                    clearable
                    solo
                    flat
                    hide-details
                    @change="onChangeDistributor"
                  >
                    <template v-slot:selection="{ item }">
                      <span class="inv-sel">{{ item.name }}</span>
                    </template>
                  </v-select>
                </v-flex>
                <v-flex md2 sm6 xs12>
                  <label class="inv-label">
                    Laboratorio
                    <v-icon
                      v-if="!filter.distId"
                      x-small
                      class="inv-label__hint"
                      title="Elija primero un distribuidor"
                      >lock</v-icon
                    >
                  </label>
                  <v-select
                    v-model="filter.labId"
                    class="inv-input"
                    :items="laboratories"
                    item-text="name"
                    item-value="id"
                    :disabled="!filter.distId"
                    :placeholder="
                      filter.distId ? 'Todos' : 'Elija un distribuidor primero'
                    "
                    no-data-text="Sin laboratorios para este distribuidor"
                    clearable
                    solo
                    flat
                    hide-details
                  >
                    <template v-slot:selection="{ item }">
                      <span class="inv-sel">{{ item.name }}</span>
                    </template>
                  </v-select>
                </v-flex>
                <v-flex md2 sm6 xs12>
                  <label class="inv-label">Desde</label>
                  <v-menu
                    v-model="modal.startDate"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.startDate"
                        v-on="on"
                        class="inv-input"
                        append-icon="event"
                        placeholder="Fecha inicial"
                        readonly
                        clearable
                        solo
                        flat
                        hide-details
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.startDate"
                      locale="es-es"
                      color="#254f87"
                      scrollable
                      @input="modal.startDate = false"
                    ></v-date-picker>
                  </v-menu>
                </v-flex>
                <v-flex md2 sm6 xs12>
                  <label class="inv-label">Hasta</label>
                  <v-menu
                    v-model="modal.endDate"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.endDate"
                        v-on="on"
                        class="inv-input"
                        append-icon="event"
                        placeholder="Fecha final"
                        readonly
                        clearable
                        solo
                        flat
                        hide-details
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.endDate"
                      locale="es-es"
                      color="#254f87"
                      scrollable
                      @input="modal.endDate = false"
                    ></v-date-picker>
                  </v-menu>
                </v-flex>
                <v-flex xs12 class="inv-filter__actions">
                  <button class="inv-btn inv-btn--ghost" @click="clearFilter">
                    <v-icon small class="inv-btn__icon">filter_alt_off</v-icon>
                    Limpiar
                  </button>
                  <button class="inv-btn inv-btn--secondary" @click="onSearch">
                    <v-icon small class="inv-btn__icon">search</v-icon>
                    Buscar
                  </button>
                  <button class="inv-btn inv-btn--export" @click="onDownload">
                    <v-icon small class="inv-btn__icon">cloud_download</v-icon>
                    Exportar
                  </button>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- ===== Trámites ===== -->
        <!-- Una sola tabla como Contabilidad: el cliente pidió que el listado
             se vea igual. Cada trámite es un <tbody> con su banda y sus
             facturas debajo. -->
        <v-flex xs12>
          <v-card class="inv-card">
            <div class="tx-toolbar">
              <div class="tx-toolbar__info">
                <span class="tx-chip">
                  <v-icon small class="tx-chip__icon">receipt_long</v-icon>
                  {{ meta.totalGroups }}
                  {{ meta.totalGroups === 1 ? 'trámite' : 'trámites' }}
                  <template v-if="meta.totalInvoices !== meta.totalGroups">
                    ({{ meta.totalInvoices }} facturas)
                  </template>
                </span>
              </div>
            </div>
            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <div class="table-wrap">
                <table class="tx-table">
                  <thead>
                    <tr>
                      <th>Laboratorio</th>
                      <th># Factura</th>
                      <th class="th-right">CIF (USD)</th>
                      <th class="th-right">Peso Bruto</th>
                      <th class="th-center">Bulto</th>
                      <th># Control</th>
                      <th>Distribuidor</th>
                      <th>Fecha de creación</th>
                      <th class="th-actions">Acción</th>
                    </tr>
                  </thead>

                  <!-- Arriba de las filas: si se recarga tras una acción, el
                       aviso queda a la vista aunque la tabla sea larga. -->
                  <tbody v-if="pagination.loading">
                    <tr>
                      <td colspan="9" class="empty-state">
                        <span class="inv-empty">
                          <span class="inv-spinner"></span>
                          Cargando facturas...
                        </span>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else-if="groups.length === 0">
                    <tr>
                      <td colspan="9" class="empty-state">
                        <span class="inv-empty">
                          <v-icon class="inv-empty__icon">inbox</v-icon>
                          No se encontraron facturas con los filtros aplicados.
                        </span>
                      </td>
                    </tr>
                  </tbody>

                  <tbody
                    v-for="group in groups"
                    :key="group.key"
                    class="tx-group"
                  >
                    <!-- Banda del trámite (texto del mixin TramiteBand). A la
                         derecha, las acciones del trámite; el texto pliega o
                         despliega sus facturas. -->
                    <tr
                      class="tx-group-row"
                      :class="{
                        'tx-group-row--single': !isConsolidated(group),
                      }"
                    >
                      <td colspan="9">
                        <div class="band">
                          <div
                            class="band__label"
                            role="button"
                            tabindex="0"
                            :aria-expanded="
                              isCollapsed(group.key) ? 'false' : 'true'
                            "
                            @click="toggle(group.key)"
                            @keydown.enter.prevent="toggle(group.key)"
                          >
                            <v-icon small class="tx-group-row__icon">{{
                              isConsolidated(group) ? 'layers' : 'description'
                            }}</v-icon>
                            {{ etiquetaTramite(group) }}
                            <strong>{{ consecutivoDe(group) }}</strong>
                            &middot; {{ facturasDe(group) }}
                            <!-- En qué paso va el trámite. Sin esto había que
                                 abrir pantalla por pantalla para saber dónde
                                 estaba un consolidado. -->
                            <span
                              class="paso-pill"
                              :class="'paso-pill--' + pasoDe(group).tono"
                              :title="pasoDe(group).ayuda"
                            >
                              {{ pasoDe(group).texto }}
                            </span>
                            <span
                              v-if="isConsolidated(group)"
                              class="tx-group-row__muted"
                            >
                              &middot; CIF total USD
                              {{ money(group.totals.cif) }} &middot;
                              {{ money(group.totals.grossWeight) }} kg &middot;
                              {{ group.totals.bultosSum }} bultos
                            </span>
                            <span
                              v-if="controlFdDe(group)"
                              class="tx-group-row__muted"
                            >
                              &middot; Control F&amp;D: {{ controlFdDe(group) }}
                            </span>
                            <span
                              v-if="group.multipleOrganos"
                              class="tx-group-row__warn"
                              :title="group.organosAnuentes.join(', ')"
                            >
                              &middot; varios órganos anuentes
                            </span>
                            <span
                              v-if="group.multipleDistribuidores"
                              class="tx-group-row__warn"
                              :title="group.distribuidores.join(', ')"
                            >
                              &middot; varios distribuidores
                            </span>
                          </div>

                          <div class="band__actions" @click.stop>
                            <button
                              v-if="
                                isConsolidated(group) && canManageConsolidation
                              "
                              class="band-btn band-btn--add"
                              title="Agregar factura a este consolidado"
                              @click="openAddTo(group)"
                            >
                              <v-icon small>playlist_add</v-icon>
                              Agregar factura
                            </button>
                            <button
                              v-if="
                                isConsolidated(group) && canManageConsolidation
                              "
                              class="band-btn band-btn--split"
                              title="Des-consolidar este trámite"
                              @click="askDeconsolidate(group)"
                            >
                              <v-icon small>call_split</v-icon>
                              Des-consolidar
                            </button>
                            <button
                              v-if="
                                !isConsolidated(group) && canManageConsolidation
                              "
                              class="band-btn band-btn--add"
                              title="Consolidar esta factura con otras"
                              @click="openAddTo(group)"
                            >
                              <v-icon small>layers</v-icon>
                              Consolidar
                            </button>
                            <button
                              class="band-toggle"
                              type="button"
                              :title="
                                isCollapsed(group.key)
                                  ? 'Mostrar facturas'
                                  : 'Ocultar facturas'
                              "
                              @click="toggle(group.key)"
                            >
                              <v-icon>{{
                                isCollapsed(group.key)
                                  ? 'expand_more'
                                  : 'expand_less'
                              }}</v-icon>
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>

                    <!-- Una fila por factura, con sus observaciones y motivos
                         de pendiente debajo. En un consolidado las tres llevan
                         tx-child-row para que la sangría no se corte. -->
                    <template v-for="item in group.invoices">
                      <tr
                        v-show="!isCollapsed(group.key)"
                        :key="item.id"
                        :class="{ 'tx-child-row': isConsolidated(group) }"
                      >
                        <td class="tx-cell--strong">
                          {{ item.laboratorio || '—' }}
                        </td>
                        <td>{{ item.invoiceNumber || '—' }}</td>
                        <td class="th-right tx-nowrap">
                          {{ money(item.cif) }}
                        </td>
                        <td class="th-right tx-nowrap">
                          {{ money(item.grossWeight) }}
                        </td>
                        <td class="th-center">{{ item.packageNumber || 0 }}</td>
                        <td>{{ item.controlNumber || '—' }}</td>
                        <td>{{ item.distribuidor || '—' }}</td>
                        <td class="tx-nowrap">{{ dateOf(item.createdAt) }}</td>
                        <td class="th-actions">
                          <div class="action-group">
                            <v-btn
                              :to="{
                                path: `/facturas/editar/${item.id}`,
                                query: {
                                  consolidated: isConsolidated(group) ? 1 : 0,
                                },
                              }"
                              class="btn-edit"
                              icon
                              small
                              title="Editar factura"
                            >
                              <v-icon small>edit</v-icon>
                            </v-btn>
                            <v-btn
                              v-if="canManageConsolidation"
                              class="btn-flag"
                              :class="{
                                'btn-flag--on':
                                  hasReasons(item) || isEditingReasons(item),
                              }"
                              icon
                              small
                              :title="
                                hasReasons(item)
                                  ? 'Editar motivos de pendiente'
                                  : 'Agregar motivo de pendiente'
                              "
                              @click="toggleReasonsEdit(item)"
                            >
                              <v-icon small>flag</v-icon>
                              <span
                                v-if="hasReasons(item)"
                                class="btn-flag__count"
                                >{{ item.pendingReasons.length }}</span
                              >
                            </v-btn>
                            <v-btn
                              v-if="
                                isConsolidated(group) && canManageConsolidation
                              "
                              class="btn-unlink"
                              icon
                              small
                              title="Quitar del consolidado"
                              @click="askRemoveFromConsolidated(item, group)"
                            >
                              <v-icon small>link_off</v-icon>
                            </v-btn>
                            <v-btn
                              v-else-if="isAdminUser && !isConsolidated(group)"
                              class="btn-delete"
                              icon
                              small
                              title="Eliminar factura"
                              @click="askDelete(item, group)"
                            >
                              <v-icon small>delete</v-icon>
                            </v-btn>
                          </div>
                        </td>
                      </tr>
                      <tr
                        v-if="item.observations"
                        v-show="!isCollapsed(group.key)"
                        :key="`obs-${item.id}`"
                        class="cons-obs-row"
                        :class="{ 'tx-child-row': isConsolidated(group) }"
                      >
                        <td colspan="9">
                          <v-icon x-small class="cons-obs__icon"
                            >sticky_note_2</v-icon
                          >
                          <span class="cons-obs__label">Observaciones:</span>
                          {{ item.observations }}
                        </td>
                      </tr>
                      <tr
                        v-if="hasReasons(item) || isEditingReasons(item)"
                        v-show="!isCollapsed(group.key)"
                        :key="`reasons-${item.id}`"
                        class="cons-obs-row cons-reasons-row"
                        :class="{ 'tx-child-row': isConsolidated(group) }"
                      >
                        <td colspan="9">
                          <v-icon x-small class="cons-obs__icon">flag</v-icon>
                          <span class="cons-obs__label">Pendiente de:</span>
                          <template
                            v-if="
                              canManageConsolidation && isEditingReasons(item)
                            "
                          >
                            <v-combobox
                              :value="
                                (item.pendingReasons || []).map(r => r.name)
                              "
                              :items="reasonNames"
                              :loading="reasonsSaving === item.id"
                              class="cons-reasons__input"
                              multiple
                              small-chips
                              deletable-chips
                              hide-details
                              dense
                              solo
                              flat
                              append-icon=""
                              placeholder="Elige un motivo o escribe uno nuevo..."
                              @change="val => onReasonsChange(item, val)"
                            ></v-combobox>
                            <button
                              class="cons-reasons__done"
                              @click="toggleReasonsEdit(item)"
                            >
                              Listo
                            </button>
                          </template>
                          <template v-else>
                            <span
                              v-for="r in item.pendingReasons"
                              :key="r.id"
                              class="cons-reason-chip"
                              >{{ r.name }}</span
                            >
                            <button
                              v-if="canManageConsolidation"
                              class="cons-reasons__edit"
                              @click="toggleReasonsEdit(item)"
                            >
                              <v-icon x-small>edit</v-icon> editar
                            </button>
                          </template>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- ===== Footer ===== -->
        <v-flex xs12 v-if="!pagination.loading && groups.length">
          <div class="list-footer">
            <div class="list-footer__count">
              Mostrando {{ groups.length }}
              {{ groups.length === 1 ? 'trámite' : 'trámites' }} de
              {{ meta.totalGroups }} ({{ meta.totalInvoices }} facturas)
            </div>
            <div class="list-footer__right">
              <v-pagination
                v-if="pagination.pages > 1"
                v-model="pagination.page"
                :length="pagination.pages"
                :total-visible="7"
                color="#254f87"
                @input="asyncPagination"
              ></v-pagination>
              <div class="list-footer__rpp">
                <span>Trámites por página:</span>
                <v-select
                  v-model="pagination.rpp"
                  :items="rppOptions"
                  class="inv-input inv-rpp"
                  dense
                  solo
                  flat
                  hide-details
                  @change="onChangeRpp"
                ></v-select>
              </div>
            </div>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog max-width="350" persistent v-model="loading">
      <loading></loading>
    </v-dialog>

    <v-dialog max-width="500" v-if="dialog.message" v-model="dialog.show">
      <message
        :key="dialog.key"
        :options="dialog"
        v-on:close="dialog.show = false"
      ></message>
    </v-dialog>

    <delete-confirm-dialog
      v-model="invoiceDelete.modal"
      :max-width="460"
      title="Eliminar factura"
      subtitle="Esta acción no se puede deshacer"
      message="Estás a punto de remover una factura permanentemente."
      warning="Este proceso no puede ser revertido."
      confirm-text="Sí, eliminar"
      @confirm="deleteInvoice"
    ></delete-confirm-dialog>

    <!-- ===== Des-consolidar: elegir cuáles facturas quitar del grupo ===== -->
    <v-dialog v-model="deconsolidate.modal" max-width="560" persistent>
      <v-card v-if="deconsolidate.group" class="add-card">
        <div class="add-card__head">
          <div>
            <h2 class="add-card__title">Des-consolidar trámite</h2>
            <p class="add-card__subtitle">
              Marca las facturas que quieres quitar del consolidado
            </p>
          </div>
          <button class="add-card__close" @click="deconsolidate.modal = false">
            <v-icon>close</v-icon>
          </button>
        </div>

        <div class="add-card__toolbar">
          <label
            class="add-item add-item--head"
            :class="{ 'add-item--on': isAllDeconsolidateSelected }"
          >
            <input
              type="checkbox"
              :checked="isAllDeconsolidateSelected"
              @change="toggleDeconsolidateAll"
            />
            <div class="add-item__main">
              <span class="add-item__inv">Seleccionar todas</span>
            </div>
          </label>
        </div>

        <div class="add-card__list">
          <label
            v-for="inv in deconsolidate.group.invoices"
            :key="inv.id"
            class="add-item"
            :class="{
              'add-item--on': deconsolidate.selected.indexOf(inv.id) > -1,
            }"
          >
            <input
              type="checkbox"
              :checked="deconsolidate.selected.indexOf(inv.id) > -1"
              @change="toggleDeconsolidate(inv.id)"
            />
            <div class="add-item__main">
              <span class="add-item__inv">{{ inv.invoiceNumber || '—' }}</span>
              <span class="add-item__meta">
                {{ inv.laboratorio || '—' }} · {{ inv.distribuidor || '—' }}
              </span>
            </div>
            <span class="add-item__cif">USD {{ money(inv.cif) }}</span>
          </label>
        </div>

        <p v-if="deconsolidate.selected.length" class="add-card__note">
          <v-icon x-small>info</v-icon>
          <template v-if="willDissolveParent">
            Se disolverá el consolidado por completo y las facturas quedarán
            independientes.
          </template>
          <template v-else>
            El consolidado seguirá existiendo con las
            {{
              deconsolidate.group.invoices.length -
                deconsolidate.selected.length
            }}
            factura(s) restante(s) y se recalcularán sus totales.
          </template>
        </p>

        <div class="add-card__footer">
          <span class="add-card__count"
            >{{ deconsolidate.selected.length }} para quitar</span
          >
          <button
            class="inv-btn inv-btn--ghost"
            @click="deconsolidate.modal = false"
          >
            Cancelar
          </button>
          <button
            class="inv-btn inv-btn--primary"
            :disabled="!deconsolidate.selected.length"
            @click="doDeconsolidate"
          >
            {{
              willDissolveParent
                ? 'Des-consolidar todo'
                : 'Quitar seleccionadas'
            }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <delete-confirm-dialog
      v-model="removeConsolidated.modal"
      :max-width="460"
      title="Quitar del consolidado"
      subtitle="La factura volverá a ser individual"
      message="Se sacará esta factura del trámite y se recalcularán los totales del consolidado."
      warning="El consolidado sigue existiendo con las demás facturas."
      confirm-text="Sí, quitar"
      @confirm="doRemoveFromConsolidated"
    ></delete-confirm-dialog>

    <!-- ===== Agregar factura a un consolidado existente ===== -->
    <v-dialog v-model="addTo.modal" max-width="640" persistent>
      <v-card class="add-card">
        <div class="add-card__head">
          <div>
            <h2 class="add-card__title">
              {{
                addTo.mode === 'create'
                  ? 'Consolidar facturas'
                  : 'Agregar factura al consolidado'
              }}
            </h2>
            <p class="add-card__subtitle">
              <template v-if="addTo.mode === 'create'">
                Se agrupará
                {{ addTo.baseNumber }}
                <template v-if="addTo.baseInternal"
                  >({{ addTo.baseInternal }})</template
                >
                con las facturas que selecciones.
              </template>
              <template v-else>
                {{ addTo.parent ? consecutivoDe(addTo.parent) : '' }}
              </template>
            </p>
          </div>
          <button class="add-card__close" @click="closeAddTo">
            <v-icon>close</v-icon>
          </button>
        </div>

        <div class="add-card__search">
          <v-text-field
            v-model="addTo.term"
            class="inv-input"
            append-icon="search"
            placeholder="Buscar factura individual por # o laboratorio..."
            solo
            flat
            hide-details
            clearable
            @keyup.enter="loadCandidates"
          ></v-text-field>
          <button class="inv-btn inv-btn--secondary" @click="loadCandidates">
            Buscar
          </button>
        </div>

        <div class="add-card__list">
          <div v-if="addTo.loading" class="add-card__state">
            <span class="inv-spinner"></span> Cargando...
          </div>
          <div v-else-if="!addTo.candidates.length" class="add-card__state">
            No se encontraron facturas individuales{{
              addTo.term ? ` para "${addTo.term}"` : ' disponibles'
            }}.
          </div>
          <label
            v-for="c in addTo.candidates"
            :key="c.id"
            class="add-item"
            :class="{ 'add-item--on': addTo.selected.indexOf(c.id) > -1 }"
          >
            <input
              type="checkbox"
              :checked="addTo.selected.indexOf(c.id) > -1"
              @change="toggleCandidate(c.id)"
            />
            <div class="add-item__main">
              <span class="add-item__inv">{{ c.invoiceNumber || '—' }}</span>
              <span class="add-item__meta">
                {{ c.laboratorio || '—' }} · {{ c.distribuidor || '—' }}
              </span>
            </div>
            <span class="add-item__cif">USD {{ money(c.cif) }}</span>
          </label>
        </div>

        <button class="add-card__create" @click="createAndRelate">
          <v-icon small>add</v-icon>
          ¿No aparece? Crear factura{{
            addTo.term ? ` "${addTo.term}"` : ' nueva'
          }}
          y relacionarla
        </button>

        <div class="add-card__footer">
          <span class="add-card__count"
            >{{ addTo.selected.length }} seleccionada(s)</span
          >
          <button class="inv-btn inv-btn--ghost" @click="closeAddTo">
            Cancelar
          </button>
          <button
            class="inv-btn inv-btn--primary"
            :disabled="!addTo.selected.length || addTo.saving"
            @click="confirmAddTo"
          >
            {{ addToButtonLabel }}
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import loading from './../dialog/DialogLoading'
import message from './../dialog/Message'
import { dateFormat } from '@/helpers/TransitHelper'
import SessionHelper from '@/helpers/SessionHelper'
import AppACL from '@/plugins/acl/ACL'
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog'
import TramiteBand from './../transit/mixins/TramiteBand'

export default {
  components: {
    loading,
    message,
    DeleteConfirmDialog,
  },
  // Banda del trámite (consecutivo CONS-/IND-, facturas, control F&D) y
  // money(), compartidos con Transit y Contabilidad.
  mixins: [TramiteBand],
  data() {
    return {
      loading: true,
      distributors: [],
      laboratories: [],
      // Catálogo de motivos de pendiente, factura que se guarda, y qué
      // facturas tienen el editor de motivos abierto.
      pendingReasons: [],
      reasonsSaving: null,
      editingReasons: {},
      filter: {
        term: '',
        labId: '',
        distId: '',
        startDate: '',
        endDate: '',
        paso: '',
      },
      // Cada paso se traduce al filtro que ya entiende el backend, el mismo
      // que usa la bandeja correspondiente de Transit.
      pasos: [
        { valor: 'to-process', texto: 'Por procesar' },
        { valor: 'fd', texto: 'En proceso' },
        { valor: 'denied', texto: 'Rechazado' },
        { valor: 'approved', texto: 'Aprobado' },
        { valor: 'delivered', texto: 'Entregado' },
      ],
      modal: {
        startDate: false,
        endDate: false,
      },
      collapsed: {},
      invoiceDelete: {
        modal: false,
        id: null,
      },
      deconsolidate: {
        modal: false,
        group: null,
        selected: [],
      },
      removeConsolidated: {
        modal: false,
        parentId: null,
        invoiceId: null,
      },
      addTo: {
        modal: false,
        // 'add' = agregar a un consolidado existente; 'create' = consolidar
        // una factura individual con otras (crea el padre).
        mode: 'add',
        parent: null,
        baseId: null,
        baseNumber: '',
        // IND- de la factura base, para reconocerla en el subtítulo.
        baseInternal: '',
        loading: false,
        saving: false,
        term: '',
        candidates: [],
        selected: [],
      },
      dialog: {
        show: null,
        key: 0,
      },
      rppOptions: [10, 25, 50, 100],
      pagination: {
        page: 1,
        rpp: this.$config.resultsPerPage,
        loading: true,
        pages: 0,
        search: {},
      },
    }
  },
  created() {
    this.loadFilters()
    this.loadPendingReasons()
    this.asyncPagination()
  },
  computed: {
    ...mapState({
      groups: state => state.invoices.groups,
      meta: state => state.invoices.meta,
    }),
    isAdminUser() {
      return AppACL.isAdmin(SessionHelper.role)
    },
    canManageConsolidation() {
      return (
        SessionHelper.role === AppACL.ROLE.GEM ||
        SessionHelper.role === AppACL.ROLE.COL ||
        SessionHelper.role === AppACL.ROLE.ADM
      )
    },
    // Todas las facturas del grupo marcadas para des-consolidar.
    isAllDeconsolidateSelected() {
      const g = this.deconsolidate.group
      if (!g || !g.invoices.length) return false
      return this.deconsolidate.selected.length === g.invoices.length
    },
    // El padre se disuelve si quedan menos de 2 facturas tras la operación.
    willDissolveParent() {
      const g = this.deconsolidate.group
      if (!g) return false
      return g.invoices.length - this.deconsolidate.selected.length < 2
    },
    addToButtonLabel() {
      const create = this.addTo.mode === 'create'
      if (this.addTo.saving) return create ? 'Consolidando...' : 'Agregando...'
      return create ? 'Consolidar' : 'Agregar'
    },
    // Nombres del catálogo de motivos para el desplegable.
    reasonNames() {
      return this.pendingReasons.map(r => r.name)
    },
  },
  methods: {
    // Un trámite está consolidado si es padre (nuevo) o consolidación F&D
    // legada. Delega en el mixin para que la banda y las acciones usen la
    // misma regla que el resto de listas.
    isConsolidated(group) {
      return this.esConsolidado(group)
    },
    // Paso en el que va el trámite, leído de sus propias facturas. Se muestra
    // el paso PENDIENTE más temprano (lo que falta por hacer), no el más
    // avanzado: en un consolidado a medias, lo útil es saber qué queda.
    // El orden es el del flujo: procesar → F&D → aprobado → entregado, con el
    // rechazo por delante porque detiene todo lo demás.
    pasoDe(group) {
      const facturas = (group && group.invoices) || []
      if (!facturas.length) return { texto: '—', tono: 'muted', ayuda: '' }

      const alguna = fn => facturas.some(fn)
      const todas = fn => facturas.every(fn)

      // Rechazo: la misma condición que usan Rechazadas y Trámites para
      // Reingreso (fdApproved = 'NO'), para que el badge y el filtro no puedan
      // discrepar. Al reingresarlo vuelve directo a Por procesar.
      if (alguna(f => f.fdApproved === 'NO')) {
        return {
          texto: 'Rechazado',
          tono: 'danger',
          ayuda:
            'Rechazado por el órgano anuente. Vive en Transit › Rechazadas y Trámites para Reingreso',
        }
      }
      if (alguna(f => f.checkCase !== 'YES')) {
        return {
          texto: 'Por procesar',
          tono: 'muted',
          ayuda:
            'Falta marcar la valija. Vive en Transit › Trámites para Procesar',
        }
      }
      if (alguna(f => f.fdApproved !== 'YES')) {
        return {
          texto: 'En proceso',
          tono: 'info',
          ayuda:
            'En revisión del órgano anuente. Vive en Transit › Trámites en Proceso',
        }
      }
      if (todas(f => f.delivered === 'YES')) {
        return {
          texto: 'Entregado',
          tono: 'success',
          ayuda: 'Entregado al cliente',
        }
      }
      return {
        texto: 'Aprobado',
        tono: 'success',
        ayuda: 'Aprobado por el órgano anuente. Sigue en Contabilidad',
      }
    },
    // DD/MM/YYYY como Contabilidad y Transit.
    dateOf(value) {
      return value ? dateFormat(value, 'DD/MM/YYYY') : '—'
    },
    isCollapsed(key) {
      return !!this.collapsed[key]
    },
    toggle(key) {
      this.$set(this.collapsed, key, !this.collapsed[key])
    },
    loadFilters() {
      this.$store.dispatch('distributors/getAll', {}).then(response => {
        this.distributors = (response.items || []).map(d => ({
          name: d.name,
          id: d.id,
        }))
      })
    },
    loadPendingReasons() {
      this.$store.dispatch('invoices/getPendingReasons').then(reasons => {
        this.pendingReasons = Array.isArray(reasons) ? reasons : []
      })
    },
    hasReasons(item) {
      return !!(item.pendingReasons && item.pendingReasons.length)
    },
    isEditingReasons(item) {
      return !!this.editingReasons[item.id]
    },
    toggleReasonsEdit(item) {
      this.$set(this.editingReasons, item.id, !this.editingReasons[item.id])
    },
    // Guarda los motivos de una factura. Los nombres que no existan en el
    // catálogo se crean al vuelo ("poder crear más").
    async onReasonsChange(item, names) {
      const list = Array.isArray(names) ? names : []
      this.reasonsSaving = item.id
      try {
        const ids = []
        for (let i = 0; i < list.length; i += 1) {
          const clean = String(list[i]).trim()
          if (!clean) continue // eslint-disable-line no-continue
          let found = this.pendingReasons.find(
            r => r.name.toLowerCase() === clean.toLowerCase()
          )
          if (!found) {
            // eslint-disable-next-line no-await-in-loop
            const res = await this.$store.dispatch(
              'invoices/createPendingReason',
              clean
            )
            if (res && res.status < 400 && res.data && res.data.id) {
              found = { id: res.data.id, name: res.data.name }
              this.pendingReasons.push(found)
            } else {
              continue // eslint-disable-line no-continue
            }
          }
          if (!ids.includes(found.id)) ids.push(found.id)
        }

        const resp = await this.$store.dispatch(
          'invoices/setInvoicePendingReasons',
          { invoiceId: item.id, reasonIds: ids }
        )
        if (!resp || resp.status >= 400) {
          this.dialog = {
            message:
              (resp && resp.data && resp.data.error) ||
              'No se pudieron guardar los motivos.',
            show: true,
            color: 'red',
            key: this.dialog.key + 1,
          }
          return
        }
        // Reflejar en el item sin recargar toda la página.
        this.$set(
          item,
          'pendingReasons',
          ids.map(id => this.pendingReasons.find(r => r.id === id))
        )
      } finally {
        this.reasonsSaving = null
      }
    },
    onChangeDistributor(id) {
      this.laboratories = []
      this.filter.labId = ''
      if (id) {
        this.$store.dispatch('distributors/getById', { id }).then(response => {
          this.laboratories = (response.laboratories || []).map(l => ({
            name: l.name,
            id: l.id,
          }))
        })
      }
    },
    asyncPagination() {
      this.pagination.loading = true
      const filter = {
        rpp: this.pagination.rpp,
        page: this.pagination.page,
        ...this.pagination.search,
      }
      return new Promise(resolve => {
        this.$store.dispatch('invoices/getConsolidated', filter).then(() => {
          this.pagination.pages =
            Math.ceil(this.meta.totalGroups / this.pagination.rpp) || 0
          this.loading = false
          this.pagination.loading = false
          return resolve()
        })
      })
    },
    onSearch() {
      const params = {
        term: this.filter.term,
        labId: this.filter.labId,
        distId: this.filter.distId,
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
        // "Por procesar" es la bandeja de pendientes (option); los demás pasos
        // son el mismo filtro por estado que usan las pantallas de Transit.
        ...(this.filter.paso === 'to-process'
          ? { option: 'to-process' }
          : { status: this.filter.paso }),
      }
      Object.keys(params).forEach(key => {
        if (params[key] == null || params[key] === '') delete params[key]
      })
      this.pagination.search = params
      this.pagination.page = 1
      this.asyncPagination()
    },
    clearFilter() {
      this.filter = {
        term: '',
        labId: '',
        distId: '',
        startDate: '',
        endDate: '',
      }
      this.laboratories = []
      this.pagination.search = {}
      this.pagination.page = 1
      this.asyncPagination()
    },
    onChangeRpp() {
      this.pagination.page = 1
      this.asyncPagination()
    },
    onDownload() {
      const filter = {
        ...this.pagination.search,
        token: SessionHelper.token,
      }
      this.$socket.emit('exportTransitInvoices', filter)
    },
    askDelete(item, group) {
      if (this.isConsolidated(group)) return
      this.invoiceDelete.id = item.id
      this.invoiceDelete.modal = true
    },
    deleteInvoice() {
      this.invoiceDelete.modal = false
      this.loading = true

      this.$store
        .dispatch('invoices/delete', { invoiceID: this.invoiceDelete.id })
        .then(response => {
          const failed =
            !response ||
            response.status >= 400 ||
            (response.data && response.data == 0)
          if (failed) {
            this.loading = false
            this.dialog = {
              message:
                (response && response.data && response.data.error) ||
                'La factura no pudo ser eliminada, contacte al soporte técnico.',
              show: true,
              color: 'red',
              key: this.dialog.key + 1,
            }
          } else {
            this.asyncPagination()
          }
        })
    },
    askDeconsolidate(group) {
      this.deconsolidate.group = group
      // Arranca vacío: el usuario marca solo la(s) factura(s) que quiere sacar.
      // "Seleccionar todas" equivale a des-consolidar el trámite completo.
      this.deconsolidate.selected = []
      this.deconsolidate.modal = true
    },
    toggleDeconsolidate(id) {
      const idx = this.deconsolidate.selected.indexOf(id)
      if (idx > -1) this.deconsolidate.selected.splice(idx, 1)
      else this.deconsolidate.selected.push(id)
    },
    toggleDeconsolidateAll() {
      const g = this.deconsolidate.group
      if (!g) return
      if (this.deconsolidate.selected.length === g.invoices.length) {
        this.deconsolidate.selected = []
      } else {
        this.deconsolidate.selected = g.invoices.map(i => i.id)
      }
    },
    askRemoveFromConsolidated(item, group) {
      this.removeConsolidated.parentId = group.parentId
      this.removeConsolidated.invoiceId = item.id
      this.removeConsolidated.modal = true
    },
    doRemoveFromConsolidated() {
      this.removeConsolidated.modal = false
      this.$store
        .dispatch('invoices/removeFromConsolidated', {
          parentId: this.removeConsolidated.parentId,
          invoicesID: [this.removeConsolidated.invoiceId],
        })
        .then(response => {
          const failed = !response || response.status >= 400
          if (failed) {
            this.dialog = {
              message:
                (response && response.data && response.data.error) ||
                'No se pudo quitar la factura del consolidado.',
              show: true,
              color: 'red',
              key: this.dialog.key + 1,
            }
            return
          }
          const dissolved = response.data && response.data.dissolved
          this.dialog = {
            message: dissolved
              ? 'Se quitó la factura y se disolvió la consolidación (quedaba una sola).'
              : 'Se quitó la factura del consolidado.',
            show: true,
            color: 'primary',
            key: this.dialog.key + 1,
          }
          this.asyncPagination()
        })
    },
    // ==== Agregar factura a un consolidado existente ====
    openAddTo(group) {
      const consolidated = this.isConsolidated(group)
      const base = group.invoices && group.invoices[0]
      this.addTo = {
        modal: true,
        mode: consolidated ? 'add' : 'create',
        parent: group,
        // En modo 'create' la factura individual es la base del nuevo consolidado.
        baseId: consolidated ? null : base && base.id,
        baseNumber: consolidated ? '' : (base && base.invoiceNumber) || '',
        baseInternal: consolidated ? '' : (base && base.internalNumber) || '',
        loading: false,
        saving: false,
        term: '',
        candidates: [],
        selected: [],
      }
      this.loadCandidates()
    },
    closeAddTo() {
      this.addTo.modal = false
    },
    loadCandidates() {
      this.addTo.loading = true
      const params = { rpp: 100, page: 1 }
      if (this.addTo.term) params.term = this.addTo.term
      this.$store.dispatch('invoices/searchGroups', params).then(groups => {
        // Solo facturas individuales (no consolidadas). En modo 'create' se
        // excluye además la propia factura base.
        this.addTo.candidates = (groups || [])
          .filter(g => !this.isConsolidated(g))
          .map(g => g.invoices[0])
          .filter(Boolean)
          .filter(inv => inv.id !== this.addTo.baseId)
        this.addTo.loading = false
      })
    },
    toggleCandidate(id) {
      const idx = this.addTo.selected.indexOf(id)
      if (idx > -1) this.addTo.selected.splice(idx, 1)
      else this.addTo.selected.push(id)
    },
    // Ir a crear una factura nueva llevando el destino de consolidación; al
    // guardarla, el formulario la relaciona automáticamente (ver Form.vue).
    createAndRelate() {
      const query = {}
      if (this.addTo.term) query.invoice = this.addTo.term
      if (this.addTo.mode === 'create') {
        query.relateWith = this.addTo.baseId
      } else {
        query.relateParent = this.addTo.parent.parentId
      }
      this.addTo.modal = false
      this.$router.push({ path: '/facturas/nuevo', query })
    },
    confirmAddTo() {
      if (!this.addTo.selected.length) return
      this.addTo.saving = true
      const isCreate = this.addTo.mode === 'create'
      // 'create': consolida la factura base con las seleccionadas (crea padre).
      // 'add': agrega las seleccionadas a un consolidado existente.
      const action = isCreate
        ? 'invoices/consolidate'
        : 'invoices/addToConsolidated'
      const payload = isCreate
        ? { invoicesID: [this.addTo.baseId, ...this.addTo.selected] }
        : {
            parentId: this.addTo.parent.parentId,
            invoicesID: this.addTo.selected,
          }
      this.$store.dispatch(action, payload).then(response => {
        this.addTo.saving = false
        const failed = !response || response.status >= 400
        if (failed) {
          this.dialog = {
            message:
              (response && response.data && response.data.error) ||
              (isCreate
                ? 'No se pudieron consolidar las facturas.'
                : 'No se pudieron agregar las facturas al consolidado.'),
            show: true,
            color: 'red',
            key: this.dialog.key + 1,
          }
          return
        }
        this.addTo.modal = false
        this.asyncPagination()
      })
    },
    doDeconsolidate() {
      const group = this.deconsolidate.group
      const selected = this.deconsolidate.selected.slice()
      if (!group || !selected.length) return
      this.deconsolidate.modal = false
      this.loading = true

      // Si se marcan todas, se disuelve el padre por completo. Si es un
      // subconjunto, se quitan solo esas (el backend disuelve el padre solo
      // si quedan menos de 2 facturas).
      const removingAll = selected.length === group.invoices.length
      const action = removingAll
        ? 'invoices/deconsolidate'
        : 'invoices/removeFromConsolidated'
      const payload = removingAll
        ? { invoicesID: selected }
        : { parentId: group.parentId, invoicesID: selected }

      this.$store.dispatch(action, payload).then(response => {
        this.loading = false
        const failed = !response || response.status >= 400
        if (failed) {
          this.dialog = {
            message:
              (response && response.data && response.data.error) ||
              'No se pudo des-consolidar el trámite.',
            show: true,
            color: 'red',
            key: this.dialog.key + 1,
          }
          return
        }
        const dissolved =
          removingAll || (response.data && response.data.dissolved)
        this.dialog = {
          message: dissolved
            ? 'Se des-consolidó el trámite por completo.'
            : 'Se quitaron las facturas seleccionadas del consolidado.',
          show: true,
          color: 'primary',
          key: this.dialog.key + 1,
        }
        this.asyncPagination()
      })
    },
  },
}
</script>

<style scoped src="./../transit/transit-list.css"></style>

<style scoped>
/* Hero, tabla, banda del trámite y pie vienen de transit-list.css (el mismo
   de Contabilidad). Aquí solo lo propio del listado de facturas. */
.inv-list-page {
  background: #f8fafc;
  min-height: 100vh;
  padding-bottom: 16px;
}

/* === Card === */
.inv-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  background: #ffffff;
}

/* === Filtro === */
.inv-filter {
  padding: 16px;
}

.inv-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
  letter-spacing: 0.01em;
}
.inv-label__hint {
  color: #94a3b8 !important;
}
/* Evita que un nombre largo desborde el select en varias líneas. */
.inv-sel {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #334155;
}

.inv-filter__actions {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}

/* === Botones === */
.inv-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.18s ease;
  text-transform: none;
}

.inv-btn__icon {
  font-size: 16px !important;
}

.inv-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.inv-btn--ghost {
  background: #ffffff;
  color: #475569;
  border-color: #e2e8f0;
}

.inv-btn--ghost:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.inv-btn--secondary {
  background: rgba(37, 79, 135, 0.1);
  color: #254f87;
  border-color: rgba(37, 79, 135, 0.3);
}

.inv-btn--secondary:hover {
  background: rgba(37, 79, 135, 0.18);
}

/* Confirmar en los diálogos (Agregar, Consolidar, Quitar seleccionadas):
   no tenía estilo y se veía como un botón gris más. */
.inv-btn--primary {
  background: #254f87;
  color: #ffffff;
  border-color: #254f87;
}

.inv-btn--primary:hover:not(:disabled) {
  background: #1a3a63;
}

.inv-btn--export {
  background: #ffffff;
  color: #16a34a;
  border-color: #bbf7d0;
}

.inv-btn--export:hover {
  background: #f0fdf4;
  border-color: #86efac;
}

/* === Banda del trámite ===
   El texto (del mixin TramiteBand) pliega o despliega las facturas; las
   acciones del trámite van a la derecha y bajan de línea si no caben. */
.tx-group-row > td {
  padding-top: 8px;
  padding-bottom: 8px;
}

.band {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 16px;
}

.band__label {
  flex: 1 1 320px;
  min-width: 0;
  line-height: 1.6;
  cursor: pointer;
}

.band__label:focus {
  outline: none;
}

.band__label:focus-visible {
  outline: 2px solid rgba(37, 79, 135, 0.4);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Paso del trámite: se lee de un vistazo junto al consecutivo y evita abrir
   pantalla por pantalla para saber dónde está un consolidado. */
.paso-pill {
  display: inline-flex;
  align-items: center;
  height: 20px;
  margin-left: 8px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: help;
}
.paso-pill--muted {
  background: rgba(148, 163, 184, 0.2);
  color: #475569;
}
.paso-pill--info {
  background: rgba(37, 79, 135, 0.12);
  color: #254f87;
}
.paso-pill--success {
  background: rgba(21, 128, 61, 0.14);
  color: #15803d;
}
.paso-pill--danger {
  background: rgba(220, 38, 38, 0.12);
  color: #b91c1c;
}

.band__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-left: auto;
}

.band-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.16s ease;
}

.band-btn .v-icon {
  font-size: 16px !important;
  color: inherit !important;
}

.band-btn--add {
  background: rgba(37, 79, 135, 0.1);
  color: #254f87;
  border: 1px solid rgba(37, 79, 135, 0.25);
}

.band-btn--add:hover {
  background: rgba(37, 79, 135, 0.18);
}

.band-btn--split {
  background: rgba(234, 88, 12, 0.1);
  color: #c2410c;
  border: 1px solid rgba(234, 88, 12, 0.25);
}

.band-btn--split:hover {
  background: rgba(234, 88, 12, 0.18);
}

.band-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #64748b;
}

.band-toggle:hover {
  background: rgba(15, 23, 42, 0.06);
}

.band-toggle .v-icon {
  color: inherit !important;
}

/* === Tabla ===
   Acción más angosta que en Transit y alineada a la derecha: aquí son
   botones de ícono. El prefijo .tx-table le gana a la regla general de
   alineación de transit-list.css. */
.tx-table .th-actions {
  width: 120px;
  text-align: right;
}

/* Encabezados en una línea: con el tamaño de Contabilidad "Peso Bruto" y
   "Fecha de creación" se partían en dos. La tabla ya desplaza en horizontal. */
.tx-table th {
  white-space: nowrap;
}

.th-actions .action-group {
  justify-content: flex-end;
}

.btn-edit {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
  box-shadow: none !important;
}

.btn-delete {
  border: 1px solid rgba(229, 37, 42, 0.25) !important;
  color: #e5252a !important;
  background: rgba(229, 37, 42, 0.07) !important;
  box-shadow: none !important;
}

.btn-delete:hover {
  background: rgba(229, 37, 42, 0.16) !important;
}

.btn-unlink {
  border: 1px solid rgba(234, 88, 12, 0.3) !important;
  color: #c2410c !important;
  background: rgba(234, 88, 12, 0.1) !important;
  box-shadow: none !important;
}

.btn-unlink:hover {
  background: rgba(234, 88, 12, 0.2) !important;
}

/* Botón de bandera (motivos de pendiente) en la columna Acción. */
.btn-flag {
  position: relative;
  color: #94a3b8 !important;
}
.btn-flag--on {
  color: #d97706 !important;
}
.btn-flag__count {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 15px;
  height: 15px;
  padding: 0 3px;
  border-radius: 8px;
  background: #d97706;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 15px;
  text-align: center;
}

/* === Observaciones y motivos de pendiente (registro para KPI) ===
   Conservan su tono ámbar también al realzar el grupo; solo se fija el
   alto para no pisar la sangría de las hijas del consolidado. */
.tx-table .cons-obs-row td {
  background: #fffbeb;
  color: #92400e;
  font-size: 13px;
  padding-top: 8px;
  padding-bottom: 8px;
}

.tx-table .tx-group:hover .cons-obs-row td {
  background: #fef3c7;
}

.cons-obs__icon {
  color: #d97706 !important;
  margin-right: 4px;
}

.cons-obs__label {
  font-weight: 700;
  margin-right: 4px;
}

.cons-reasons-row .cons-reasons__input {
  display: inline-flex;
  min-width: 340px;
  max-width: 560px;
  margin-top: 2px;
  vertical-align: middle;
}
.cons-reason-chip {
  display: inline-block;
  margin: 2px 4px 2px 0;
  padding: 2px 10px;
  border-radius: 12px;
  background: #fef3c7;
  color: #92400e;
  font-size: 12px;
  font-weight: 600;
}
.cons-reasons__edit,
.cons-reasons__done {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  vertical-align: middle;
}
.cons-reasons__edit {
  color: #254f87;
  background: transparent;
  border: none;
}
.cons-reasons__edit:hover {
  background: rgba(37, 79, 135, 0.08);
}
.cons-reasons__done {
  color: #fff;
  background: #254f87;
  border: none;
}
.cons-reasons__done:hover {
  background: #1e4270;
}

/* === Estados (cargando / sin resultados) === */
.inv-empty {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #64748b;
}

.inv-empty__icon {
  color: #cbd5e1 !important;
  font-size: 22px !important;
}

.inv-spinner {
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(37, 79, 135, 0.3);
  border-top-color: #254f87;
  border-radius: 50%;
  animation: inv-spin 0.7s linear infinite;
  display: inline-block;
}

@keyframes inv-spin {
  to {
    transform: rotate(360deg);
  }
}

/* === Diálogo: agregar factura al consolidado === */
.add-card {
  border-radius: 14px !important;
  overflow: hidden;
}
.add-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 22px 12px;
}
.add-card__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}
.add-card__subtitle {
  margin: 3px 0 0;
  font-size: 13px;
  font-weight: 700;
  color: #254f87;
}
.add-card__close {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
}
.add-card__search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 22px 12px;
}
.add-card__search .inv-input {
  flex: 1;
}
.add-card__list {
  max-height: 340px;
  overflow-y: auto;
  padding: 0 22px;
  border-top: 1px solid #eef2f7;
  border-bottom: 1px solid #eef2f7;
}
.add-card__state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 0;
  color: #64748b;
  font-size: 13px;
}
.add-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 4px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
}
.add-item:last-child {
  border-bottom: none;
}
.add-item--on {
  background: rgba(37, 79, 135, 0.05);
}
.add-item input {
  width: 16px;
  height: 16px;
  accent-color: #254f87;
  cursor: pointer;
}
.add-item__main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.add-item__inv {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}
.add-item__meta {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.add-item__cif {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
}
.add-card__footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px 18px;
}
.add-card__count {
  margin-right: auto;
  font-size: 12.5px;
  color: #64748b;
}
.add-card .inv-btn {
  height: 38px;
}
.add-card__toolbar {
  padding: 4px 22px 0;
}
.add-item--head {
  border-bottom: none;
  padding-bottom: 4px;
}
.add-item--head .add-item__inv {
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.add-card__create {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: calc(100% - 44px);
  margin: 12px 22px 0;
  padding: 10px 14px;
  border: 1px dashed #b9c6da;
  border-radius: 8px;
  background: #f8fafc;
  color: #254f87;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.add-card__create:hover {
  background: #eef3fb;
  border-color: #254f87;
}
.add-card__create .v-icon {
  color: #254f87;
}
.add-card__note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 12px 22px 0;
  font-size: 12.5px;
  line-height: 1.4;
  color: #64748b;
}
.add-card__note .v-icon {
  color: #94a3b8;
}

/* === Footer === */
.list-footer {
  flex-wrap: wrap;
}

.list-footer__right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.list-footer__rpp {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
}

.inv-rpp {
  width: 80px;
}

@media (max-width: 959px) {
  .inv-filter__actions {
    justify-content: stretch;
  }
  .inv-filter__actions .inv-btn {
    flex: 1;
  }
}
</style>

<style>
/* === Inputs (clases globales de Vuetify, no scoped) === */
.inv-list-page
  .inv-input.v-text-field.v-text-field--solo
  .v-input__control
  > .v-input__slot,
.inv-list-page
  .inv-input.v-select.v-text-field--solo
  .v-input__control
  > .v-input__slot {
  background: #ffffff !important;
  border: 1px solid #d1d5db !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  min-height: 42px !important;
}

.inv-list-page
  .inv-input.v-input--is-focused
  .v-input__control
  > .v-input__slot {
  border-color: #254f87 !important;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12) !important;
}

.inv-list-page .inv-input .v-input__icon .v-icon {
  color: #94a3b8 !important;
  font-size: 19px !important;
}

/* Selección de los v-select en una sola línea (no crece en alto). */
.inv-list-page .inv-input .v-select__selections {
  flex-wrap: nowrap !important;
  min-width: 0;
}
.inv-list-page .inv-input .v-select__selections input {
  min-width: 0;
}

.inv-list-page .inv-rpp .v-select__selection {
  color: #334155 !important;
}
</style>
